import {useEffect, useState} from 'react';

const DeviceSourceId = 'devices';

const DeviceLayers = {
  Line: 'device-line',
  Points: 'device-points',
  PointsSelected: 'device-points-selected',
  PointsTemp: 'device-points-temp',
};

const createDeviceSymbolLayer = (id, size = 18, color = ['get', 'color']) => ({
  id,
  type: 'symbol',
  source: DeviceSourceId,
  layout: {
    'text-field': ['get', 'icon'],
    'text-font': ['Font Awesome 5 Pro Solid', 'Font Awesome 5 Brands Regular'],
    'text-allow-overlap': true,
    'icon-optional': true,
    'text-size': size,
  },
  paint: {
    'text-translate-anchor': 'viewport',
    'text-color': color,
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
});

const deviceLayer = createDeviceSymbolLayer(DeviceLayers.Points);
const selectedDeviceLayer = createDeviceSymbolLayer(
  DeviceLayers.PointsSelected,
  28,
  '#3AA9DF',
);
const deviceTempLayer = createDeviceSymbolLayer(DeviceLayers.PointsTemp, 28);

const showPointerCursor = (map) => (map.getCanvas().style.cursor = 'pointer');
const hidePointerCursor = (map) => (map.getCanvas().style.cursor = '');
const createFilter = (id, diff) => [diff ? '!=' : '==', 'id', id || 0];

const useMapLayers = (
  map,
  loaded,
  geojsonUrl,
  setSelectedDevice,
  dependencies,
) =>
  useEffect(() => {
    const onMouseMove = (event) => {
      const features = map.queryRenderedFeatures(event.point, {
        layers: [DeviceLayers.Points, DeviceLayers.PointsTemp],
      });
      if (!features.length) {
        hidePointerCursor(map);
        map.setFilter(DeviceLayers.PointsTemp, createFilter());
        map.setFilter(
          DeviceLayers.Points,
          createFilter(map._selectedDeviceId, true),
        );
      } else {
        showPointerCursor(map);
        const featureId = features[0].id;
        map.setFilter(DeviceLayers.PointsTemp, createFilter(featureId));
        map.setFilter(DeviceLayers.Points, [
          'all',
          createFilter(map._selectedDeviceId, true),
          createFilter(featureId, true),
        ]);
      }
    };

    if (map && loaded) {
      map.addSource(DeviceSourceId, {
        type: 'geojson',
        data: geojsonUrl,
      });
      map.addLayer({
        id: DeviceLayers.Line,
        type: 'line',
        source: DeviceSourceId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 1,
        },
      });
      map.addLayer(deviceLayer);
      map.addLayer({
        ...selectedDeviceLayer,
        filter: createFilter(),
      });
      map.addLayer({
        ...deviceTempLayer,
        filter: createFilter(),
      });
      map.on('mousemove', onMouseMove);
    }
    return () => {
      if (map) {
        Object.values(DeviceLayers).forEach((layerId) => {
          if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
          }
        });
        if (map.getSource(DeviceSourceId)) {
          map.removeSource(DeviceSourceId);
        }
        map.off('mousemove', onMouseMove);
      }
    };
  }, dependencies);

const useSelectDevice = (map, loaded) => {
  const [selectedDevice, setSelectedDevice] = useState();
  const setLayerFilter = (layer, id, diff) => {
    if (map.getLayer(layer)) {
      map.setFilter(layer, createFilter(id, diff));
    }
  };
  const clearSelectedLayer = () => {
    setLayerFilter(DeviceLayers.PointsSelected);
    setLayerFilter(DeviceLayers.Points, 0, true);
    map._selectedDeviceId = 0;
  };
  const onSelectDevice = (event) => {
    const deselectedFeatures = map.queryRenderedFeatures(event.point, {
      layers: [DeviceLayers.Points, DeviceLayers.PointsTemp],
    });
    const selectedFeatures = map.queryRenderedFeatures(event.point, {
      layers: [DeviceLayers.PointsSelected],
    });
    if (deselectedFeatures.length) {
      const feature = deselectedFeatures[0].properties;
      if (map._selectedDeviceId !== feature.id) {
        setLayerFilter(DeviceLayers.PointsSelected, feature.id);
        setLayerFilter(DeviceLayers.PointsTemp, 0);
        setLayerFilter(DeviceLayers.Points, feature.id, true);
        setSelectedDevice(feature);
        map._selectedDeviceId = feature.id;
        return;
      }
    }
    if (selectedFeatures.length) {
      setSelectedDevice();
    }
  };
  const showCursor = () => showPointerCursor(map);
  const hideCursor = () => hidePointerCursor(map);
  useEffect(() => {
    if (map && loaded) {
      if (!selectedDevice) {
        clearSelectedLayer();
      }
      map.on('click', onSelectDevice);
      map.on('mousemove', DeviceLayers.PointsSelected, showCursor);
      map.on('mouseleave', DeviceLayers.PointsSelected, hideCursor);
    }
    return () => {
      if (map) {
        map.off('click', onSelectDevice);
        map.off('mousemove', DeviceLayers.PointsSelected, showCursor);
        map.off('mouseleave', DeviceLayers.PointsSelected, hideCursor);
      }
    };
  }, [map, loaded, selectedDevice]);
  return [selectedDevice, setSelectedDevice];
};

export const useDevicesLayer = (map, loaded, geojsonUrl, dependencies = []) => {
  const [selectedDevice, setSelectedDevice] = useSelectDevice(map, loaded);
  useMapLayers(map, loaded, geojsonUrl, setSelectedDevice, dependencies);
  // mouse events
  return [selectedDevice, setSelectedDevice];
};
